<template>
  <wui-modal
    :title="sessionLabels.headerText"
    :size="40"
    :closable="true"
    class="!w-[440px] phone:!w-4/5"
    @close="close"
  >
    <div class="grid justify-items-center px-6 phone:px-4">
      <p
        v-if="groups.length"
        class="mb-11 mt-6 line-clamp-3 w-full wrap-break-word text-center text-2432"
      >
        {{ groups.join(', ') }}
      </p>

      <p :class="['mb-9 rounded-full p-4 text-white', sessionLabels.iconBgClass]">
        <wui-icon
          class="h-12! w-12!"
          :name="sessionLabels.iconName"
        />
      </p>

      <div :class="['grid text-center', contactPositionToDisplay ? 'mb-8' : 'mb-14']">
        <p class="w-full truncate text-2432">
          {{ displayName || number }}
        </p>
        <p
          v-if="contactPositionToDisplay"
          class="mt-2 w-full truncate text-1316 text-fg-base-fg22"
        >
          {{ contactPositionToDisplay }}
        </p>
      </div>

      <p class="mb-4 text-1424">
        {{ sessionLabels.infoText }}
      </p>

      <div class="mb-10 w-80 phone:w-full">
        <user-feature component-name="LeaveMessageInput">
          <leave-message-input
            v-model.trim="textMessage"
            @focus="clear"
          />
        </user-feature>
      </div>
    </div>

    <template #footer>
      <wui-btn
        variant="brand"
        state="filled"
        icon
        prepend-icon="checkRegularM"
        class="w-[120px]"
        @click="close"
      >
        <span class="sr-only select-none">{{ $t('common.closeWindow') }}</span>
      </wui-btn>
    </template>
  </wui-modal>
</template>

<script lang="ts" setup>
import { closeDialogKey, IconName, WuiIcon, WuiBtn, WuiModal, safeInject, useRenderNotifier } from '@wui/common-library'
import { computed, onMounted, onUnmounted, ref, toRef } from 'vue'

import LeaveMessageInput from '@/components/Featureable/LeaveMessageInput/LeaveMessageInput.vue'

import { useCallSessionRoomStore } from '@/entities/call-session'

import { UserFeature } from '@/shared/components/user-feature'
import { useLocalization } from '@/shared/composables/localization'
import { useSearchGroups } from '@/shared/composables/search-handlers'
import { useRoom } from '@/shared/composables/use-room'
import { RTCSessionFacade, STATE } from '@/shared/services/jssip'
import { Contact } from '@/shared/types/api'
import { createLinksFromText } from '@/shared/utils/create-links-from-text'

const props = defineProps<{
  rtcSessionFacade: RTCSessionFacade;
}>()

const closeDialog = safeInject(closeDialogKey)
const { t } = useLocalization()

const session = toRef(props, 'rtcSessionFacade')
const displayName = session.value.displayName.value === 'Anonymous' ? t('common.numberIsHidden') : session.value.displayName.value
const number = session.value.number
const groups = useSearchGroups(number)
const room = computed(() => useCallSessionRoomStore().getRoom(session.value.sessionId))
const contactPositionToDisplay = computed<Contact['position'] | false | undefined>(
  () => {
    return (
      !room.value?.isGroup &&
      room.value?.subscribers.values().next().value?.position // value is undefined when subscribers map is empty (external contact rejects our call)

    )
  },
)

const sessionLabels = computed<{
  headerText: string
  infoText: string
  iconName: IconName
  iconBgClass: string
}>(() => {
  switch (props.rtcSessionFacade.sessionState.value) {
  case STATE.UNAVAILABLE:
    return {
      headerText: t('common.callHasBeenCanceled'),
      infoText: t('common.subscriberMissedCall'),
      iconName: 'callEnd',
      iconBgClass: 'bg-black-400',
    }
  case STATE.BUSY:
    return {
      headerText: t('common.lineIsBusy'),
      infoText: t('common.subscriberLineIsBusy'),
      iconName: 'phoneEnd',
      iconBgClass: 'bg-threshold',
    }
  case STATE.REJECTED:
    return {
      headerText: t('common.subscriberUnavailable'),
      infoText: t('common.callRejectedBySubscriber'),
      iconName: 'phoneDownM',
      iconBgClass: 'bg-warning-shades-800',
    }
  case STATE.OFFLINE:
    return {
      headerText: t('common.subscriberUnavailable'),
      infoText: t('common.userNotOnline'),
      iconName: 'callCanceled',
      iconBgClass: 'bg-warning-shades-800',
    }
  case STATE.ERROR:
    return {
      headerText: t('common.error'),
      infoText: t('common.errorOccuredDialNumber'),
      iconName: 'clear',
      iconBgClass: 'bg-warning-shades-800',
    }
  default:
    return {
      headerText: t('common.error'),
      infoText: t('common.errorOccuredDialNumber'),
      iconName: 'clear',
      iconBgClass: 'bg-warning-shades-800',
    }
  }
})

const textMessage = ref('')
const roomApi = useRoom()
const { notifyError } = useRenderNotifier()

const terminate = () => {
  session.value.terminate()
  closeDialog(true)
}

const close = () => {
  const room = useCallSessionRoomStore().getRoom(props.rtcSessionFacade.sessionId)
  if (room && textMessage.value) {
    roomApi.send(createLinksFromText(textMessage.value), room)
      .catch((e: Error) => {
        notifyError(e.message)
      })
  }

  terminate()
}

let timeoutId: ReturnType<typeof setTimeout>

const clear = () => {
  clearTimeout(timeoutId)
}

onMounted(() => {
  timeoutId = setTimeout(() => {
    terminate()
  }, 5000)
})

onUnmounted(() => {
  clear()
})
</script>
